import axios, { HttpStatusCode } from 'axios';

/**
 * True when `error` is an Axios error carrying an HTTP 401/403 response.
 *
 * Distinguishes "the identity authority has refused this user" (unauthorized /
 * forbidden) from transient network or 5xx failures, so callers can lock the
 * user out only on a genuine authorization denial.
 */
export function isForbiddenError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  return status === HttpStatusCode.Forbidden || status === HttpStatusCode.Unauthorized;
}

/**
 * True when `error` is an Axios error carrying an HTTP 409 response.
 *
 * The status the API answers with when the request is well-formed but conflicts with
 * what is already stored — a location that already has a split, a party still
 * referenced by one. Distinguishing it from the generic failure is what lets a caller
 * say *which* rule was broken instead of "please try again": the body's message is the
 * server's English, so the caller pairs the status with its own translated copy.
 */
export function isConflictError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Conflict;
}

/**
 * The `{ message }` an API error response carries, when there is one.
 *
 * The backend answers a rejected request with that shape — including the message an
 * upstream system gave for refusing it ("SurName is required"), which is far more use
 * to the caller than a generic "something went wrong". Undefined for network failures
 * and for responses without a message, so callers can fall back to their own copy.
 */
export function apiErrorMessage(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const message = (error.response?.data as { message?: unknown } | undefined)?.message;
  return typeof message === 'string' && message.trim() !== '' ? message : undefined;
}
